import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonSearchbar,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonChip,
    IonModal,
    IonTextarea,
    IonItem,
    IonInput,
    IonSelect,
    IonSelectOption,
    useIonRouter
} from '@ionic/react';
import {
    shieldCheckmarkOutline,
    warningOutline,
    banOutline,
    checkmarkCircleOutline,
    eyeOutline,
    createOutline,
    trashOutline,
    flagOutline,
    timeOutline,
    personOutline,
    chatbubbleOutline,
    alertCircleOutline,
    closeCircleOutline,
    chevronBackOutline,
    chevronForwardOutline
} from 'ionicons/icons';
import { useState, useEffect, useRef } from 'react';
import './View.css';

const AdminModeration = () => {
    const router = useIonRouter();
    const modal = useRef(null);

    const [reports, setReports] = useState([]);
    const [filteredReports, setFilteredReports] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('all');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);
    const [moderationAction, setModerationAction] = useState('');
    const [moderationNote, setModerationNote] = useState('');

    const [stats, setStats] = useState({
        totalReports: 0,
        pending: 0,
        resolved: 0,
        escalated: 0
    });
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 5;

    // Mock moderation data
    const mockReports = [
        {
            id: 1,
            type: 'poll',
            contentId: 'poll_123',
            title: 'Inappropriate Poll Content',
            content: 'What is the worst thing about people?',
            reportedBy: 'user_456',
            reporterName: 'John Doe',
            reason: 'Hate Speech',
            description: 'This poll contains offensive language and promotes negativity',
            status: 'pending',
            priority: 'high',
            reportDate: '2024-08-10T10:30:00Z',
            assignedTo: null,
            category: 'content_violation'
        },
        {
            id: 2,
            type: 'poll',
            contentId: 'poll_456',
            title: 'Spam Poll',
            content: 'Click here to win $1000!!!',
            reportedBy: 'user_789',
            reporterName: 'Jane Smith',
            reason: 'Spam',
            description: 'This appears to be spam content promoting external links',
            status: 'under_review',
            priority: 'medium',
            reportDate: '2024-08-09T15:45:00Z',
            assignedTo: 'admin_001',
            category: 'spam'
        },
        {
            id: 3,
            type: 'user',
            contentId: 'user_999',
            title: 'Abusive User Behavior',
            content: 'User has been creating multiple offensive polls',
            reportedBy: 'user_111',
            reporterName: 'Mike Wilson',
            reason: 'Harassment',
            description: 'This user consistently creates polls with inappropriate content',
            status: 'resolved',
            priority: 'high',
            reportDate: '2024-08-08T09:15:00Z',
            assignedTo: 'admin_002',
            category: 'user_behavior',
            resolution: 'User account suspended for 7 days'
        }
    ];

    useEffect(() => {
        fetchModerationReports();
    }, []);

    useEffect(() => {
        filterReports();
    }, [reports, searchText, selectedFilter]);

    const fetchModerationReports = () => {
        setReports(mockReports);

        // Calculate stats
        const total = mockReports.length;
        const pending = mockReports.filter(r => r.status === 'pending').length;
        const underReview = mockReports.filter(r => r.status === 'under_review').length;
        const resolved = mockReports.filter(r => r.status === 'resolved').length;
        const escalated = mockReports.filter(r => r.priority === 'high' && r.status !== 'resolved').length;

        setStats({ totalReports: total, pending, resolved, escalated });
    };

    const filterReports = () => {
        let filtered = reports;

        // Filter by status
        if (selectedFilter !== 'all') {
            filtered = filtered.filter(report => {
                switch (selectedFilter) {
                    case 'pending': return report.status === 'pending';
                    case 'under_review': return report.status === 'under_review';
                    case 'resolved': return report.status === 'resolved';
                    case 'high_priority': return report.priority === 'high';
                    default: return true;
                }
            });
        }

        // Filter by search text
        if (searchText) {
            filtered = filtered.filter(report =>
                report.title.toLowerCase().includes(searchText.toLowerCase()) ||
                report.content.toLowerCase().includes(searchText.toLowerCase()) ||
                report.reason.toLowerCase().includes(searchText.toLowerCase())
            );
        }

        setFilteredReports(filtered);
    };

    const handleModerationAction = (action, report) => {
        setSelectedReport(report);
        setModerationAction(action);
        setIsModalOpen(true);
    };

    const executeModerationAction = () => {
        console.log('Executing moderation action:', {
            reportId: selectedReport.id,
            action: moderationAction,
            note: moderationNote
        });

        // Update report status based on action
        const updatedReports = reports.map(report => {
            if (report.id === selectedReport.id) {
                let newStatus = 'under_review';
                if (moderationAction === 'approve') newStatus = 'resolved';
                if (moderationAction === 'remove' || moderationAction === 'suspend') newStatus = 'resolved';

                return {
                    ...report,
                    status: newStatus,
                    assignedTo: 'current_admin',
                    resolution: moderationNote,
                    resolutionAction: moderationAction // Store the specific action taken
                };
            }
            return report;
        });

        setReports(updatedReports);
        // Also update filtered reports to immediately reflect changes in UI
        setFilteredReports(prevFiltered => prevFiltered.map(report => {
            if (report.id === selectedReport.id) {
                let newStatus = 'under_review';
                if (moderationAction === 'approve') newStatus = 'resolved';
                if (moderationAction === 'remove' || moderationAction === 'suspend') newStatus = 'resolved';

                return {
                    ...report,
                    status: newStatus,
                    assignedTo: 'current_admin',
                    resolution: moderationNote,
                    resolutionAction: moderationAction
                };
            }
            return report;
        }));

        setIsModalOpen(false);
        setModerationNote('');
        setModerationAction('');
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'under_review': return 'primary';
            case 'resolved': return 'success';
            default: return 'medium';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'danger';
            case 'medium': return 'warning';
            case 'low': return 'success';
            default: return 'medium';
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'content_violation': return warningOutline;
            case 'spam': return banOutline;
            case 'user_behavior': return personOutline;
            default: return flagOutline;
        }
    };

    // Pagination computed values
    const totalPages = Math.ceil(filteredReports.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedReports = filteredReports.slice(startIndex, endIndex);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <IonPage className="view-page">
            <IonHeader className="ion-no-border">
                <IonToolbar className="header-toolbar">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" />
                    </IonButtons>
                    <IonTitle className="page-title">
                        <div className="title-container">
                            <span className="title-text">Content Moderation</span>
                        </div>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton fill="clear">
                            <IonIcon icon={shieldCheckmarkOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content">
                <div className="polls-container">

                    {/* Moderation Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm">Total Reports</p>
                                    <p className="text-2xl font-bold">{stats.totalReports}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    <IonIcon icon={flagOutline} className="text-xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-orange-100 text-sm">Pending</p>
                                    <p className="text-2xl font-bold">{stats.pending}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    <IonIcon icon={timeOutline} className="text-xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm">Resolved</p>
                                    <p className="text-2xl font-bold">{stats.resolved}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    <IonIcon icon={checkmarkCircleOutline} className="text-xl" />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white shadow-lg">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-sm">High Priority</p>
                                    <p className="text-2xl font-bold">{stats.escalated}</p>
                                </div>
                                <div className="bg-white bg-opacity-20 rounded-full p-3">
                                    <IonIcon icon={alertCircleOutline} className="text-xl" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Quick Actions</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <button className="flex items-center justify-center space-x-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                <IonIcon icon={warningOutline} />
                                <span className="font-medium text-sm">Review High Priority</span>
                            </button>
                            <button className="flex items-center justify-center space-x-2 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                <IonIcon icon={timeOutline} />
                                <span className="font-medium text-sm">Process Pending</span>
                            </button>
                            <button className="flex items-center justify-center space-x-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                <IonIcon icon={flagOutline} />
                                <span className="font-medium text-sm">Content Guidelines</span>
                            </button>
                            <button className="flex items-center justify-center space-x-2 bg-green-50 hover:bg-green-100 text-green-600 rounded-xl py-3 px-4 transition-colors duration-200">
                                <IonIcon icon={shieldCheckmarkOutline} />
                                <span className="font-medium text-sm">Moderation Log</span>
                            </button>
                        </div>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                        <div className="flex flex-col-reverse md:flex-col gap-4">
                            <div className="flex md:justify-end items-center space-x-4 w-full">
                                <IonSegment value={selectedFilter} onIonChange={(e) => setSelectedFilter(e.detail.value)}>
                                    <IonSegmentButton value="all">
                                        <IonLabel>All</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="pending">
                                        <IonLabel>Pending</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="under_review">
                                        <IonLabel>In Review</IonLabel>
                                    </IonSegmentButton>
                                    <IonSegmentButton value="high_priority">
                                        <IonLabel>High Priority</IonLabel>
                                    </IonSegmentButton>
                                </IonSegment>
                            </div>
                            <div className="w-full">
                                <IonSearchbar
                                    value={searchText}
                                    onIonInput={(e) => setSearchText(e.detail.value)}
                                    placeholder="Search reports by content, reason, or reporter..."
                                    className="custom-searchbar"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Reports List */}
                    <div className="space-y-4">
                        {paginatedReports.map((report) => (
                            <div key={report.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                                <div className="p-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-start space-x-4 flex-1">
                                            <div className="bg-gray-100 rounded-full p-3">
                                                <IonIcon icon={getCategoryIcon(report.category)} className="text-xl text-gray-600" />
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center space-x-2 mb-2">
                                                    <h3 className="text-lg font-bold text-gray-800">{report.title}</h3>
                                                    <IonChip color={getStatusColor(report.status)} className="text-xs">
                                                        {report.status.replace('_', ' ').toUpperCase()}
                                                    </IonChip>
                                                    <IonChip color={getPriorityColor(report.priority)} className="text-xs">
                                                        {report.priority.toUpperCase()} PRIORITY
                                                    </IonChip>
                                                </div>

                                                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                                                    <p className="text-sm text-gray-700 font-medium mb-1">Reported Content:</p>
                                                    <p className="text-sm text-gray-600 italic">"{report.content}"</p>
                                                </div>

                                                <div className="space-y-1 text-sm text-gray-600">
                                                    <div><strong>Reason:</strong> {report.reason}</div>
                                                    <div><strong>Reported by:</strong> {report.reporterName}</div>
                                                    <div><strong>Date:</strong> {new Date(report.reportDate).toLocaleString()}</div>
                                                    {report.description && (
                                                        <div><strong>Description:</strong> {report.description}</div>
                                                    )}
                                                    {report.resolution && (
                                                        <div className="bg-green-50 p-2 rounded mt-2">
                                                            <strong className="text-green-700">Resolution:</strong>
                                                            <span className="text-green-600 ml-1">{report.resolution}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex flex-col space-y-2 ml-4">
                                            {report.status !== 'resolved' && (
                                                <div className="flex flex-col space-y-2 ml-4 min-w-[140px]">
                                                    <IonButton
                                                        expand="block"
                                                        color="success"
                                                        className="font-bold tracking-wide"
                                                        onClick={() => handleModerationAction('approve', report)}
                                                    >
                                                        <IonIcon icon={checkmarkCircleOutline} slot="start" />
                                                        APPROVE
                                                    </IonButton>
                                                    <IonButton
                                                        expand="block"
                                                        color="danger"
                                                        className="font-bold tracking-wide"
                                                        onClick={() => handleModerationAction('reject', report)}
                                                    >
                                                        <IonIcon icon={closeCircleOutline} slot="start" />
                                                        REJECT
                                                    </IonButton>
                                                    <IonButton
                                                        expand="block"
                                                        fill="outline"
                                                        color="warning"
                                                        className="font-bold tracking-wide border-2"
                                                        onClick={() => handleModerationAction('investigate', report)}
                                                    >
                                                        <IonIcon icon={eyeOutline} slot="start" />
                                                        INVESTIGATE
                                                    </IonButton>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex justify-end">
                                            <IonIcon icon={eyeOutline} className="text-orange-400 text-xl cursor-pointer" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-xl shadow-lg p-4 mt-6 gap-3">
                            <div className="text-sm text-gray-600 order-2 sm:order-1">
                                Showing {startIndex + 1} - {Math.min(endIndex, filteredReports.length)} of {filteredReports.length} reports
                            </div>
                            <div className="flex items-center space-x-2 order-1 sm:order-2">
                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === 1}
                                    onClick={() => goToPage(currentPage - 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronBackOutline} slot="icon-only" />
                                </IonButton>

                                {[...Array(totalPages)].map((_, i) => (
                                    <IonButton
                                        key={i + 1}
                                        fill={currentPage === i + 1 ? 'solid' : 'outline'}
                                        size="small"
                                        onClick={() => goToPage(i + 1)}
                                        style={{
                                            '--border-radius': '8px',
                                            minWidth: '36px',
                                            display: (totalPages <= 5 || i < 2 || i >= totalPages - 2 || Math.abs(i + 1 - currentPage) <= 1) ? 'inline-flex' : 'none'
                                        }}
                                    >
                                        {i + 1}
                                    </IonButton>
                                ))}

                                <IonButton
                                    fill="outline"
                                    size="small"
                                    disabled={currentPage === totalPages}
                                    onClick={() => goToPage(currentPage + 1)}
                                    style={{ '--border-radius': '8px' }}
                                >
                                    <IonIcon icon={chevronForwardOutline} slot="icon-only" />
                                </IonButton>
                            </div>
                        </div>
                    )}

                    {filteredReports.length === 0 && (
                        <div className="text-center py-12">
                            <div className="text-6xl mb-4">🛡️</div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No Reports Found</h3>
                            <p className="text-gray-600">All clear! No moderation reports match your criteria.</p>
                        </div>
                    )}
                </div>

                {/* Moderation Action Modal */}
                <IonModal ref={modal} isOpen={isModalOpen} onDidDismiss={() => setIsModalOpen(false)} className="moderation-modal">
                    <IonHeader className="ion-no-border">
                        <IonToolbar>
                            <IonTitle className="font-bold text-lg">Moderation Action</IonTitle>
                            <IonButtons slot="end">
                                <IonButton onClick={() => setIsModalOpen(false)} color="dark">CANCEL</IonButton>
                            </IonButtons>
                        </IonToolbar>
                    </IonHeader>
                    <IonContent className="ion-padding">
                        {selectedReport && (
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800 mb-4">Report Details</h3>
                                    <div className="bg-gray-50 rounded-xl p-6 space-y-3 shadow-sm border border-gray-100">
                                        <div className="text-gray-800"><span className="font-bold text-gray-900">Title:</span> {selectedReport.title}</div>
                                        <div className="text-gray-800"><span className="font-bold text-gray-900">Content:</span> "{selectedReport.content}"</div>
                                        <div className="text-gray-800"><span className="font-bold text-gray-900">Reason:</span> {selectedReport.reason}</div>
                                        <div className="text-gray-800"><span className="font-bold text-gray-900">Reporter:</span> {selectedReport.reporterName}</div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="relative">
                                        <IonSelect
                                            value={moderationAction}
                                            onIonChange={(e) => setModerationAction(e.detail.value)}
                                            placeholder="Select Action"
                                            interface="popover"
                                            className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2 text-gray-700"
                                            style={{ '--placeholder-opacity': '0.6' }}
                                        >
                                            <IonSelectOption value="approve">Approve Content (No Violation)</IonSelectOption>
                                            <IonSelectOption value="remove">Remove Content</IonSelectOption>
                                            <IonSelectOption value="warn">Warn User</IonSelectOption>
                                            <IonSelectOption value="suspend">Suspend User</IonSelectOption>
                                            <IonSelectOption value="investigate">Needs Further Investigation</IonSelectOption>
                                        </IonSelect>
                                    </div>
                                </div>

                                <div>
                                    <textarea
                                        value={moderationNote}
                                        onChange={(e) => setModerationNote(e.target.value)}
                                        placeholder="Add moderation notes or reason for action..."
                                        rows={4}
                                        className="w-full border border-gray-200 rounded-xl p-4 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none"
                                    />
                                </div>

                                <div className="flex space-x-4 pt-4">
                                    <button
                                        onClick={executeModerationAction}
                                        disabled={!moderationAction}
                                        className={`px-6 py-3 rounded-md font-bold text-white tracking-wide shadow-md transition-colors ${moderationAction ? 'bg-orange-500 hover:bg-orange-600' : 'bg-gray-300 cursor-not-allowed'
                                            }`}
                                    >
                                        EXECUTE ACTION
                                    </button>
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-6 py-3 rounded-md font-bold text-orange-500 border border-orange-500 hover:bg-orange-50 transition-colors tracking-wide"
                                    >
                                        CANCEL
                                    </button>
                                </div>
                            </div>
                        )}
                    </IonContent>
                </IonModal>
            </IonContent>
        </IonPage >
    );
};

export default AdminModeration;
